<template>
    <div v-if="isLoading">
        <b-row>
            <b-col md="6">
                <atom-spinner
                    :animation-duration="1000"
                    :size="60"
                    color="#ff1d5e"
                    class="float-right my-4"
                />
            </b-col>
        </b-row>
    </div>
    <div v-else class="animated fadeIn">
        <b-col md="12">
            <component-header
                title="Permission"
                :isLoading="isLoading"
                :hasSearch="true"
                :hasReload="true"
                @search-query="(val) => (filter = val)"
                @reload-data="reloadData"
                createButtonText="Create Permission"
                @create-press="onClickAddPermission"
            />
        </b-col>
        <b-row>
            <b-col>
                <b-card border-variant="info" class="card-wrap">
                    <div>
                        <b-table
                            id="my-table"
                            class="mt-3 professional-stacked-table"
                            :items="permissions"
                            :fields="permissionColumns"
                            :filter="filter"
                            :current-page="currentPage"
                            :per-page="perPage"
                            @filtered="onFilter"
                            responsive
                            hover
                            :bordered="true"
                            :dark="dark"
                            thead-class="header"
                            show-empty
                        >
                            <template slot="cell(actions)" slot-scope="data">
                                <div class="inline_btnTable">
                                    <b-button
                                        class="button-circle btn-warning"
                                        variant="primary"
                                        @click="editPermission(data)"
                                    >
                                        <i class="fa fa-pencil edit"></i>
                                    </b-button>
                                    <b-button
                                        class="button-circle"
                                        variant="danger"
                                        @click="deletePermission(data)"
                                    >
                                        <i class="fa fa-trash bin"></i>
                                    </b-button>
                                </div>
                            </template>
                            <template slot="cell(row-details)" slot-scope="row">
                                <b-card class="role-details">
                                    <div>
                                        <multiselect
                                            :id="row.item._id"
                                            :value="permissionMap[row.item._id]"
                                            :options="allPermissions"
                                            label="permission"
                                            track-by="permission"
                                            :multiple="true"
                                            @select="
                                                (selectedOption, id) =>
                                                    addPermissionToRole(
                                                        selectedOption,
                                                        id,
                                                        row,
                                                    )
                                            "
                                        ></multiselect>
                                    </div>
                                </b-card>
                            </template>
                        </b-table>
                        <b-modal
                            v-model="permissionModal"
                            header-class="mb-3 header-text"
                            hide-footer
                            id="roleModal"
                            centered
                        >
                            <div slot="modal-header">{{ header }}</div>
                            <div>
                                <b-row>
                                    <b-col cols="12" sm="12" md="6">
                                        <div class="form-group">
                                            <b-input
                                                class="input-height-40"
                                                v-model="permission.name"
                                            />
                                        </div>
                                    </b-col>
                                    <b-col cols="12" sm="12" md="6">
                                        <div class="form-group">
                                            <multiselect
                                                class="perm_input"
                                                :options="methods"
                                                placeholder="Select a Method"
                                                label="value"
                                                track-by="value"
                                                :multiple="false"
                                                v-model="permission.action"
                                            ></multiselect>
                                        </div>
                                    </b-col>
                                    <b-col cols="12" sm="12" md="12">
                                        <div class="form-group">
                                            <b-input
                                                class="input-height-40"
                                                v-model="permission.resource"
                                            />
                                        </div>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col class="text-center">
                                        <div class="cancelSaveBtn permission-modal-footer-btns">
                                            <b-button
                                                @click="clearModal"
                                                variant="danger"
                                                class="btn-width-100 danger"
                                                >Cancel</b-button
                                            >
                                            <b-button
                                                @click="handleEdit"
                                                variant="primary"
                                                class="btn-width-100"
                                                >Save</b-button
                                            >
                                        </div>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-modal>
                        <div style="display: flex; justify-content: flex-end;">
                            <b-pagination
                                v-model="currentPage"
                                :per-page="perPage"
                                :total-rows="filterCount || permissions.length"
                                aria-controls="my-table"
                                prev-text="Prev"
                                next-text="Next"
                                hide-goto-end-buttons
                            />
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>
        <b-modal
            id="AddPermission"
            size="md"
            ref="add-permission"
            centered
            header-class="mb-3 header-text"
            hide-footer
        >
            <div slot="modal-header">Add Permission</div>
            <create-permission
                @newPerm="hide"
                @newPermission="insertPerm"
                ref="permissionComp"
            />
        </b-modal>
    </div>
</template>

<style lang="sass" scoped>
.card-wrap
  box-shadow: 0 0 10px #ccc
  .role-details
    margin: 10px
</style>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import { AtomSpinner } from "epic-spinners";
import {
    createPermission,
    editPermission,
    listPermissions,
} from "@/services/auth";
import CreatePermission from "@/components/Auth/CreatePermission.vue";
import { isValidPermission } from "@/models/IPermission";
import { authMixin } from "@/auth/mixins";

export default {
    name: "Permission",
    components: {
        CreatePermission,
        AtomSpinner,
        ComponentHeader,
    },
    mixins: [authMixin],
    props: {
        auth: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            filterCount: 0,
            filter: "",
            perPage: 10,
            currentPage: 1,
            dark: false,
            header: "Create a Permission",
            permissions: [],
            permission: {},
            permissionModal: false,
            permissionColumns: [
                {
                    key: "resource",
                    label: "Resource",
                    thStyle: { width: "30%" },
                },
                {
                    key: "name",
                    label: "Permission",
                    thStyle: { width: "30%" },
                },
                {
                    key: "action",
                    label: "Method",
                    thStyle: { width: "20%" },
                },
                {
                    key: "actions",
                    label: "Actions",
                    thClass: "customer-col-action",
                    tdClass: "customer-col-action",
                    thStyle: { width: "20%" },
                },
            ],
            methods: [
                {
                    label: "GET",
                    value: "GET",
                },
                {
                    label: "POST",
                    value: "POST",
                },
                {
                    label: "PUT",
                    value: "PUT",
                },
                {
                    label: "DELETE",
                    value: "DELETE",
                },
            ],
            permissionMap: {},
            allPermissions: [],
        };
    },
    methods: {
        async insertPerm(item) {
            try {
                const result = await createPermission(item);
                this.permissions.unshift(result.response);
                this.$swal({
                    title: "Success",
                    text: "Permission created successfully!!",
                    type: "success",
                });
                this.closeAddPermissionModal();
            } catch (error) {
                this.$toasted.error(error.message || "Failed to create permission");
            }
        },

        async hide() {
            this.closeAddPermissionModal();
        },

        closeAddPermissionModal() {
            this.$refs["add-permission"].hide();
            this.$refs["permissionComp"].clearRefresh();
        },

        async deletePermission({ item }) {
            const result = await this.$swal({
                title: "Are you sure?",
                text: "You can't revert this action!!",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                type: "warning",
                confirmButtonText: "Delete",
                cancelButtonColor: "#d33",
                reverseButtons: true,
            });
            
            if (result.value) {
                try {
                    await this.$store.dispatch("permission/removePermission", item._id);
                    this.$toasted.success("Permission deleted successfully!");
                    this.listAllPermissions();
                } catch (error) {
                    this.$toasted.error(error.message || "Failed to delete permission");
                }
            }
        },

        onFilter(list) {
            this.filterCount = list.length;
            this.currentPage = 1;
        },

        clearModal() {
            this.permission = {};
            this.permissionModal = false;
        },

        async handleEdit() {
            try {
                if (!isValidPermission(this.permission)) {
                    return;
                }
                const { name, resource, action } = this.permission;
                const payload = {
                    name,
                    resource,
                    action: action.value,
                };
                await editPermission(this.permission._id, payload);
                this.updatePermissionInList(payload);
                this.$toasted.success("Permission edited successfully!");
                this.clearModal();
            } catch (error) {
                this.$toasted.error(error.message || "Failed to edit permission");
            }
        },

        updatePermissionInList(payload) {
            this.permissions = this.permissions.map((p) =>
                p._id === this.permission._id
                    ? {
                          ...payload,
                          _id: this.permission._id,
                      }
                    : p,
            );
        },

        editPermission(row) {
            this.permission = {
                ...row.item,
                action: {
                    label: row.item.action,
                    value: row.item.action,
                },
            };
            this.header = "Edit a Permission";
            this.permissionModal = true;
        },

        onClickAddPermission() {
            this.$refs["add-permission"].show();
        },

        reloadData() {
            this.resetFilters();
            this.listAllPermissions();
        },

        resetFilters() {
            this.filter = "";
            this.filterCount = 0;
            this.currentPage = 1;
        },

        async listAllPermissions() {
            this.isLoading = true;
            try {
                const result = await listPermissions();
                this.permissions = result.response.reverse();
            } catch (error) {
                this.$toasted.error(error.message || "Failed to load permissions");
            } finally {
                this.isLoading = false;
            }
        },

        addPermissionToRole(selectedOption, id, row) {
            // This method should be implemented based on your requirements
            // It's referenced in the template but not defined in the original class
        },
    },
    mounted() {
        this.listAllPermissions();
    },
};
</script>
<style scoped>
.edit {
    color: white !important;
}

.rounded-card-permission {
    border-radius: 15px;
}

.rounded-table {
    border-radius: 12px;
}

.btn-warning {
    background: #fe7b08 !important;
    border-color: #fe7b08 !important;
}

.input-height-40 {
    height: 40px;
}

.btn-width-100 {
    width: 100px;
}

tbody tr:nth-child(2n + 1) {
    border-bottom: 1px solid #e8e5e5;
}

.resource-header,
.resource-cell {
    padding-left: 0.25rem !important;
}

.header-text {
    text-align: center !important;
}

.header-text .modal-title,
.header-text > div {
    width: 100%;
    text-align: center !important;
    margin: 0 auto;
    display: block;
}

/* Edit a Permission modal: Cancel and Save alignment + spacing */
.permission-modal-footer-btns {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 1rem;
}

.permission-modal-footer-btns .btn {
    margin: 0 !important;
}

@media (max-width: 576px) {
    .permission-modal-footer-btns {
        flex-direction: row;
        gap: 0.75rem;
    }

    .permission-modal-footer-btns .btn {
        flex: 1 1 0;
        min-width: 100px;
        max-width: 140px;
    }
}

.inline_btnTable {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
}

.inline_btnTable .button-circle {
    flex-shrink: 0;
    margin-right: 0 !important;
    margin-left: 0 !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    padding: 0 !important;
    border-radius: 50% !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
}

@media (max-width: 575.98px) {
    .inline_btnTable {
        gap: 4px;
    }
}

</style>
